import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PortsDataGrid = ({ searchQuery, onEdit, onDelete, selectedItems, onSelectionChange }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const portsData = [
    {
      id: 'PORT001',
      name: 'Port of Newcastle',
      code: 'AUNEW',
      country: 'Australia',
      type: 'Export',
      capacity: 185000000,
      currentUtilization: 78,
      draftLimit: 18.5,
      berthCount: 3,
      operationalStatus: 'operational',
      weatherRestrictions: 'Cyclone season Nov-Apr',
      lastMaintenance: '2025-11-15',
      syncStatus: 'synced'
    },
    {
      id: 'PORT002',
      name: 'Port of Brisbane',
      code: 'AUBNE',
      country: 'Australia',
      type: 'Export',
      capacity: 95000000,
      currentUtilization: 65,
      draftLimit: 15.5,
      berthCount: 2,
      operationalStatus: 'operational',
      weatherRestrictions: 'Cyclone season Nov-Apr',
      lastMaintenance: '2025-10-20',
      syncStatus: 'synced'
    },
    {
      id: 'PORT003',
      name: 'Port of Gladstone',
      code: 'AUGLT',
      country: 'Australia',
      type: 'Export',
      capacity: 125000000,
      currentUtilization: 82,
      draftLimit: 17.2,
      berthCount: 4,
      operationalStatus: 'operational',
      weatherRestrictions: 'Cyclone season Nov-Apr',
      lastMaintenance: '2025-12-01',
      syncStatus: 'pending'
    },
    {
      id: 'PORT004',
      name: 'Port of Hay Point',
      code: 'AUHPT',
      country: 'Australia',
      type: 'Export',
      capacity: 155000000,
      currentUtilization: 88,
      draftLimit: 18.0,
      berthCount: 3,
      operationalStatus: 'maintenance',
      weatherRestrictions: 'Cyclone season Nov-Apr',
      lastMaintenance: '2025-12-10',
      syncStatus: 'synced'
    },
    {
      id: 'PORT005',
      name: 'Mundra Port',
      code: 'INMUN',
      country: 'India',
      type: 'Import',
      capacity: 200000000,
      currentUtilization: 72,
      draftLimit: 16.5,
      berthCount: 5,
      operationalStatus: 'operational',
      weatherRestrictions: 'Monsoon season Jun-Sep',
      lastMaintenance: '2025-11-28',
      syncStatus: 'synced'
    }
  ];

  const filteredData = portsData?.filter(port =>
    port?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    port?.code?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    port?.country?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = [...filteredData]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(sortedData?.map(item => item?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, id]);
    } else {
      onSelectionChange(selectedItems?.filter(itemId => itemId !== id));
    }
  };

  const formatCapacity = (capacity) => {
    return `${(capacity / 1000000)?.toFixed(0)}M MT/yr`;
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'synced': return 'text-success';
      case 'pending': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left">
              <Checkbox
                checked={selectedItems?.length === sortedData?.length && sortedData?.length > 0}
                onChange={(e) => handleSelectAll(e?.target?.checked)}
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('code')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Code
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Port Name
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('capacity')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Capacity
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Utilization</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Specifications</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sync</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedData?.map((port) => (
            <tr key={port?.id} className="hover:bg-muted/30 transition-colors">
              <td className="px-4 py-3">
                <Checkbox
                  checked={selectedItems?.includes(port?.id)}
                  onChange={(e) => handleSelectItem(port?.id, e?.target?.checked)}
                />
              </td>
              <td className="px-4 py-3">
                <span className="font-mono text-sm font-medium">{port?.code}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">{port?.name}</span>
                  <span className="text-xs text-muted-foreground">{port?.country}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  port?.type === 'Export' ?'bg-primary/10 text-primary' :'bg-accent/10 text-accent'
                }`}>
                  <Icon name={port?.type === 'Export' ? 'ArrowUpRight' : 'ArrowDownLeft'} size={12} />
                  {port?.type}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm font-medium">{formatCapacity(port?.capacity)}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden min-w-[60px]">
                    <div
                      className={`h-full transition-all ${
                        port?.currentUtilization >= 85 ? 'bg-error' :
                        port?.currentUtilization >= 70 ? 'bg-warning' : 'bg-success'
                      }`}
                      style={{ width: `${port?.currentUtilization}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{port?.currentUtilization}%</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs"><span className="font-medium">Draft:</span> {port?.draftLimit}m</span>
                  <span className="text-xs"><span className="font-medium">Berths:</span> {port?.berthCount}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  port?.operationalStatus === 'operational' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    port?.operationalStatus === 'operational' ? 'bg-success' : 'bg-warning'
                  }`} />
                  {port?.operationalStatus}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Icon
                    name={port?.syncStatus === 'synced' ? 'CheckCircle2' : port?.syncStatus === 'pending' ? 'Clock' : 'AlertCircle'}
                    size={16}
                    className={getSyncStatusColor(port?.syncStatus)}
                  />
                  <span className={`text-xs ${getSyncStatusColor(port?.syncStatus)}`}>
                    {port?.syncStatus}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEdit(port)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onDelete(port?.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No ports found matching your search</p>
        </div>
      )}
    </div>
  );
};

export default PortsDataGrid;